/**
 * Spark "Apply a skill" controller (web, M2.4).
 *
 * Opens a compact skill picker (Default + My Skills), and on selection runs the
 * chosen skill against the currently-open CV via {@link SkillRunDialog}: the
 * same dry-run → accept/reject → real-run flow as the Hub.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { SkillSummary } from '@tabbio/types';
import { HugeSymbol as MaterialSymbol } from '../icons/HugeSymbol';
import { useLocale } from '../../hooks/useLocale';
import { listSkills } from '../../lib/skills-api';
import { SkillTierBadge, useSkillAuthorLabel } from './skill-ui.web';
import { SkillRunDialog } from './SkillRunDialog.web';
import { useDefaultCvId } from './use-default-cv-id';

interface Props {
  /** The open CV. Falls back to the default CV when not provided. */
  cvId?: string | null;
  onClose: () => void;
  /** Called once a skill run completes successfully. */
  onApplied?: () => void;
}

export function SkillApplyController({ cvId, onClose, onApplied }: Props) {
  const { t } = useTranslation('common');
  const { isRTL, locale } = useLocale();
  const authorLabel = useSkillAuthorLabel();
  const fallback = useDefaultCvId(!cvId);
  const effectiveCvId = cvId ?? fallback.cvId;

  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SkillSummary | null>(null);
  const reqRef = useRef(0);

  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    const reqId = ++reqRef.current;
    setLoading(true);
    Promise.all([
      listSkills({ shelf: 'default', q: query || undefined, locale, limit: 24 }),
      listSkills({ shelf: 'own', q: query || undefined, locale, limit: 24 }),
    ])
      .then(([def, own]) => {
        if (reqId !== reqRef.current) return;
        const seen = new Set<string>();
        const merged: SkillSummary[] = [];
        for (const s of [...def.skills, ...own.skills]) {
          if (seen.has(s.id)) continue;
          seen.add(s.id);
          merged.push(s);
        }
        setSkills(merged);
      })
      .catch(() => {
        if (reqId === reqRef.current) setSkills([]);
      })
      .finally(() => {
        if (reqId === reqRef.current) setLoading(false);
      });
  }, [query, locale]);

  const empty = useMemo(() => !loading && skills.length === 0, [loading, skills]);

  // While a skill is selected, the run dialog (its own portal) takes over.
  if (selected) {
    return (
      <SkillRunDialog
        skill={selected}
        cvId={effectiveCvId}
        onClose={() => setSelected(null)}
        onComplete={() => {
          onApplied?.();
          onClose();
        }}
      />
    );
  }

  const picker = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('skills.picker.title')}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        fontFamily: "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
      onClick={onClose}
    >
      <section
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 520,
          width: '100%',
          maxHeight: 'min(720px, calc(100vh - 40px))',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 28,
          backgroundColor: 'var(--menu-bg)',
          boxShadow: 'var(--gem-dialog-shadow)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px 22px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                }}
              >
                {t('skills.picker.title')}
              </h2>
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 13,
                  color: 'var(--text-tertiary)',
                }}
              >
                {t('skills.picker.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('actions.close')}
              style={{
                width: 34,
                height: 34,
                borderRadius: 17,
                border: 'none',
                background: 'rgba(0,0,0,0.04)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <MaterialSymbol name="close" size={18} />
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              height: 44,
              borderRadius: 9999,
              paddingInline: 16,
              background: 'rgba(0,0,0,0.04)',
              marginTop: 14,
            }}
          >
            <MaterialSymbol
              name="search"
              size={18}
              style={{ color: 'var(--text-secondary)' }}
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('skills.searchPlaceholder')}
              dir="auto"
              aria-label={t('skills.searchPlaceholder')}
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                color: 'var(--text-primary)',
                fontSize: 14,
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 12px 16px' }}>
          {loading ? (
            <div
              style={{
                padding: '40px 0',
                display: 'flex',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  border: '2px solid rgba(0,0,0,0.12)',
                  borderTopColor: 'var(--text-secondary)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            </div>
          ) : empty ? (
            <p
              style={{
                padding: '32px 12px',
                textAlign: 'center',
                fontSize: 14,
                color: 'var(--text-secondary)',
              }}
            >
              {t('skills.picker.empty')}
            </p>
          ) : (
            skills.map((skill) => (
              <button
                key={skill.id}
                type="button"
                onClick={() => setSelected(skill)}
                style={rowStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0,0,0,0.04)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {skill.name}
                    </span>
                    <SkillTierBadge tier={skill.tier} />
                  </div>
                  <div
                    dir="auto"
                    style={{
                      fontSize: 13,
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {skill.description}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    {authorLabel(skill)}
                  </div>
                </div>
                <MaterialSymbol
                  name={isRTL ? 'chevron_left' : 'chevron_right'}
                  size={20}
                  style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
                />
              </button>
            ))
          )}
        </div>
      </section>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(picker, document.body);
}

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '12px 12px',
  borderRadius: 12,
  border: 'none',
  background: 'transparent',
  textAlign: 'start',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background-color 150ms',
};
